/**
 * Insights API Layer.
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type {
  DemandForecastReport,
  InsightsSummary,
  PricingRecommendation,
  SlowMoversReport,
  TurnoverReport,
} from "@/types/insights";

export function usePricingRecommendation(assetId: string | undefined) {
  return useQuery<PricingRecommendation>({
    queryKey: ["insights", "pricing", assetId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/insights/pricing/${assetId}`))
        .data as PricingRecommendation,
    enabled: !!assetId,
  });
}

export function useSlowMovers(threshold = 2.0) {
  return useQuery<SlowMoversReport>({
    queryKey: ["insights", "slow-movers", threshold],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/insights/slow-movers?threshold=${threshold}`,
        )
      ).data as SlowMoversReport,
  });
}

export function useDemandForecast() {
  return useQuery<DemandForecastReport>({
    queryKey: ["insights", "demand-forecast"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/insights/demand-forecast"))
        .data as DemandForecastReport,
  });
}

export function useTurnover(months = 12) {
  return useQuery<TurnoverReport>({
    queryKey: ["insights", "turnover", months],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/insights/turnover?months=${months}`))
        .data as TurnoverReport,
  });
}

export function useInsightsSummary() {
  return useQuery<InsightsSummary>({
    queryKey: ["insights", "summary"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/insights/summary")).data as InsightsSummary,
  });
}
